import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import { interviewService } from "../services";
import {
  FiPlusCircle,
  FiX,
  FiBriefcase,
  FiAward,
  FiGlobe,
  FiHash,
  FiLink,
  FiFileText,
  FiZap,
  FiSettings,
  FiCheckCircle,
} from "react-icons/fi";

const CreateInterview = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formLoading, setFormLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    profession: "",
    type: "ai_generated",
    difficulty: "mid",
    language: "en",
    questionCount: 5,
    companyLink: "",
    linkDescription: "",
  });
  const [customQuestions, setCustomQuestions] = useState([
    { id: 1, text: "" },
    { id: 2, text: "" },
    { id: 3, text: "" },
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCustomQuestionChange = (id, value) => {
    setCustomQuestions((prev) =>
      prev.map((q) => (q.id === id ? { ...q, text: value } : q))
    );
  };

  const addCustomQuestion = () => {
    const newId = Math.max(...customQuestions.map((q) => q.id), 0) + 1;
    setCustomQuestions((prev) => [...prev, { id: newId, text: "" }]);
  };

  const removeCustomQuestion = (id) => {
    if (customQuestions.length > 1) {
      setCustomQuestions((prev) => prev.filter((q) => q.id !== id));
    }
  };

  const handleCreateInterview = async (e) => {
    e.preventDefault();

    if (!formData.title.trim() || !formData.profession.trim()) {
      toast.warning(t("errors.fillAllFields"));
      return;
    }

    if (formData.type === "ai_generated_link" && !formData.companyLink.trim()) {
      toast.warning(t("errors.provideCompanyLink"));
      return;
    }

    if (formData.type === "custom") {
      const filledQuestions = customQuestions.filter((q) => q.text.trim());
      if (filledQuestions.length === 0) {
        toast.warning("Por favor, escribe al menos una pregunta");
        return;
      }
    }

    const userStr = localStorage.getItem("user");
    const user = userStr ? JSON.parse(userStr) : null;
    const isPremium = user?.subscriptionStatus === "premium";
    const maxQuestions = isPremium ? 50 : 10;
    const questionCount = parseInt(formData.questionCount) || 5;

    if (formData.type !== "custom" && questionCount > maxQuestions) {
      toast.error(
        `${
          isPremium ? "Premium" : "Free"
        } users can create up to ${maxQuestions} questions.`
      );
      return;
    }
    if (formData.type !== "custom" && questionCount < 1) {
      toast.warning(t("errors.atLeastOneQuestion"));
      return;
    }

    setFormLoading(true);
    try {
      let questions = [];

      if (formData.type === "ai_generated") {
        toast.info(t("interview.generatingQuestions"));
        const questionsResponse = await interviewService.generateQuestions({
          profession: formData.profession,
          difficulty: formData.difficulty,
          language: formData.language,
          count: questionCount,
        });
        questions = questionsResponse.data.questions;

        if (!questions || questions.length === 0) {
          toast.error(t("errors.questionGenerationFailed"));
          setFormLoading(false);
          return;
        }
        toast.success(
          t("interview.questionsGenerated", { count: questions.length })
        );
      } else if (formData.type === "ai_generated_link") {
        toast.info(t("interview.generatingQuestionsFromLink"));
        const questionsResponse =
          await interviewService.generateQuestionsFromLink({
            companyLink: formData.companyLink,
            linkDescription: formData.linkDescription,
            difficulty: formData.difficulty,
            language: formData.language,
            count: questionCount,
          });
        questions = questionsResponse.data.questions;

        if (!questions || questions.length === 0) {
          toast.error(t("errors.questionGenerationFailedFromLink"));
          setFormLoading(false);
          return;
        }
        toast.success(
          t("interview.questionsGeneratedFromLink", { count: questions.length })
        );
      } else if (formData.type === "custom") {
        // Manual questions
        questions = customQuestions
          .filter((q) => q.text.trim())
          .map((q) => q.text.trim());

        if (questions.length === 0) {
          toast.warning("Por favor, escribe al menos una pregunta");
          setFormLoading(false);
          return;
        }

        toast.success(`${questions.length} preguntas personalizadas creadas`);
      }

      const response = await interviewService.createInterview({
        title: formData.title,
        profession: formData.profession,
        type: formData.type,
        difficulty: formData.difficulty,
        language: formData.language,
        questions: questions,
        companyLink: formData.companyLink || null,
        linkDescription: formData.linkDescription || null,
      });

      toast.success(t("interview.createSuccess"));

      // Redirect to the new interview session page
      setTimeout(() => {
        navigate(
          `/interview/${
            response.data.interview.id || response.data.interview._id
          }`
        );
      }, 1000);
    } catch (error) {
      console.error("Error creating interview:", error);
      toast.error(
        error.response?.data?.message || t("errors.createInterviewError")
      );
    } finally {
      setFormLoading(false);
    }
  };

  const userStr = localStorage.getItem("user");
  const user = userStr ? JSON.parse(userStr) : null;
  const isPremium = user?.subscriptionStatus === "premium";

  return (
    <div className="minimal-create-interview">
      {/* Hero Section */}
      <motion.div
        className="create-hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="create-hero-overlay"></div>
        <motion.div
          className="create-hero-content"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <h1 className="create-hero-title">{t("interview.newInterview")}</h1>
          <p className="create-hero-subtitle">
            Configura tu entrevista personalizada en minutos
          </p>
        </motion.div>
      </motion.div>

      {/* Form Container */}
      <div className="create-content">
        <motion.div
          className="create-form-container"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <form onSubmit={handleCreateInterview} className="create-form">
            {/* Basic Information Section */}
            <div className="form-section">
              <div className="section-header">
                <FiFileText className="section-icon" />
                <div>
                  <h3 className="section-title">Información Básica</h3>
                  <p className="section-subtitle">
                    Define los datos principales de tu entrevista
                  </p>
                </div>
              </div>

              <div className="form-row">
                <motion.div
                  className="form-group"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <label className="form-label">
                    <FiFileText className="label-icon" />
                    Título de la entrevista
                  </label>
                  <input
                    type="text"
                    name="title"
                    placeholder={t("interview.titlePlaceholder")}
                    value={formData.title}
                    onChange={handleChange}
                    className="form-control"
                    required
                    disabled={formLoading}
                  />
                </motion.div>

                <motion.div
                  className="form-group"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 }}
                >
                  <label className="form-label">
                    <FiBriefcase className="label-icon" />
                    Profesión
                  </label>
                  <input
                    type="text"
                    name="profession"
                    placeholder={t("interview.professionPlaceholder")}
                    value={formData.profession}
                    onChange={handleChange}
                    className="form-control"
                    required
                    disabled={formLoading}
                  />
                </motion.div>
              </div>
            </div>

            {/* Interview Type Section */}
            <div className="form-section">
              <div className="section-header">
                <FiZap className="section-icon" />
                <div>
                  <h3 className="section-title">Tipo de Entrevista</h3>
                  <p className="section-subtitle">
                    Selecciona cómo quieres generar las preguntas
                  </p>
                </div>
              </div>

              <motion.div
                className="form-group"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
              >
                <div className="interview-type-grid">
                  <div
                    className={`type-card ${
                      formData.type === "ai_generated" ? "active" : ""
                    }`}
                    onClick={() =>
                      !formLoading &&
                      setFormData({ ...formData, type: "ai_generated" })
                    }
                  >
                    <FiZap className="type-icon" />
                    <h4 className="type-title">
                      {t("interview.type_ai_generated")}
                    </h4>
                    <p className="type-description">
                      IA genera preguntas estándar basadas en tu profesión
                    </p>
                    {formData.type === "ai_generated" && (
                      <FiCheckCircle className="type-check" />
                    )}
                  </div>

                  <div
                    className={`type-card ${
                      formData.type === "ai_generated_link" ? "active" : ""
                    }`}
                    onClick={() =>
                      !formLoading &&
                      setFormData({ ...formData, type: "ai_generated_link" })
                    }
                  >
                    <FiLink className="type-icon" />
                    <h4 className="type-title">
                      {t("interview.type_ai_generated_link")}
                    </h4>
                    <p className="type-description">
                      IA analiza una oferta de trabajo específica
                    </p>
                    {formData.type === "ai_generated_link" && (
                      <FiCheckCircle className="type-check" />
                    )}
                  </div>

                  <div
                    className={`type-card ${
                      formData.type === "custom" ? "active" : ""
                    }`}
                    onClick={() =>
                      !formLoading &&
                      setFormData({ ...formData, type: "custom" })
                    }
                  >
                    <FiSettings className="type-icon" />
                    <h4 className="type-title">{t("interview.type_custom")}</h4>
                    <p className="type-description">
                      Crea tus propias preguntas manualmente
                    </p>
                    {formData.type === "custom" && (
                      <FiCheckCircle className="type-check" />
                    )}
                  </div>
                </div>
              </motion.div>

              {/* Conditional Company Link Fields */}
              <AnimatePresence mode="wait">
                {formData.type === "ai_generated_link" && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="form-group">
                      <label className="form-label">
                        <FiLink className="label-icon" />
                        Enlace de la empresa
                      </label>
                      <input
                        type="url"
                        name="companyLink"
                        placeholder={t("interview.companyLinkPlaceholder")}
                        value={formData.companyLink}
                        onChange={handleChange}
                        className="form-control"
                        required
                        disabled={formLoading}
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        <FiFileText className="label-icon" />
                        Descripción (opcional)
                      </label>
                      <textarea
                        name="linkDescription"
                        placeholder={t("interview.linkDescriptionPlaceholder")}
                        value={formData.linkDescription}
                        onChange={handleChange}
                        className="form-control form-textarea"
                        disabled={formLoading}
                        rows="3"
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Custom Questions Section */}
              <AnimatePresence mode="wait">
                {formData.type === "custom" && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="custom-questions-container"
                  >
                    <div className="custom-questions-header">
                      <h4 className="custom-questions-title">
                        Tus Preguntas Personalizadas
                      </h4>
                      <p className="custom-questions-subtitle">
                        Escribe las preguntas que quieres que te haga la IA
                      </p>
                    </div>

                    <div className="custom-questions-list">
                      {customQuestions.map((question, index) => (
                        <motion.div
                          key={question.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{ duration: 0.2 }}
                          className="custom-question-item"
                        >
                          <div className="question-number">{index + 1}</div>
                          <input
                            type="text"
                            placeholder={`Pregunta ${
                              index + 1
                            }: ¿Qué quieres que te pregunte?`}
                            value={question.text}
                            onChange={(e) =>
                              handleCustomQuestionChange(
                                question.id,
                                e.target.value
                              )
                            }
                            className="form-control custom-question-input"
                            disabled={formLoading}
                          />
                          {customQuestions.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeCustomQuestion(question.id)}
                              className="remove-question-btn"
                              disabled={formLoading}
                            >
                              <FiX />
                            </button>
                          )}
                        </motion.div>
                      ))}
                    </div>

                    <button
                      type="button"
                      onClick={addCustomQuestion}
                      className="add-question-btn"
                      disabled={formLoading}
                    >
                      <FiPlusCircle />
                      <span>Agregar otra pregunta</span>
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Configuration Section */}
            <div className="form-section">
              <div className="section-header">
                <FiSettings className="section-icon" />
                <div>
                  <h3 className="section-title">Configuración</h3>
                  <p className="section-subtitle">
                    Personaliza los parámetros de tu entrevista
                  </p>
                </div>
              </div>

              <div className="form-row">
                <motion.div
                  className="form-group"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                >
                  <label className="form-label">
                    <FiAward className="label-icon" />
                    Dificultad
                  </label>
                  <select
                    name="difficulty"
                    value={formData.difficulty}
                    onChange={handleChange}
                    className="form-control"
                    disabled={formLoading}
                  >
                    <option value="junior">
                      {t("interview.difficulty_junior")}
                    </option>
                    <option value="mid">{t("interview.difficulty_mid")}</option>
                    <option value="senior">
                      {t("interview.difficulty_senior")}
                    </option>
                  </select>
                </motion.div>

                <motion.div
                  className="form-group"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.9 }}
                >
                  <label className="form-label">
                    <FiGlobe className="label-icon" />
                    Idioma
                  </label>
                  <select
                    name="language"
                    value={formData.language}
                    onChange={handleChange}
                    className="form-control"
                    disabled={formLoading}
                  >
                    <option value="en">English</option>
                    <option value="es">Español</option>
                    <option value="fr">Français</option>
                    <option value="de">Deutsch</option>
                  </select>
                </motion.div>

                {(formData.type === "ai_generated" ||
                  formData.type === "ai_generated_link") && (
                  <motion.div
                    className="form-group"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.0 }}
                  >
                    <label className="form-label">
                      <FiHash className="label-icon" />
                      Número de preguntas
                    </label>
                    <select
                      name="questionCount"
                      value={formData.questionCount}
                      onChange={handleChange}
                      className="form-control"
                      disabled={formLoading}
                    >
                      <option value={3}>3 {t("interview.questions")}</option>
                      <option value={5}>5 {t("interview.questions")}</option>
                      <option value={10}>
                        10 {t("interview.questions")} ({t("common.free_max")})
                      </option>
                      {isPremium && (
                        <>
                          <option value={15}>
                            15 {t("interview.questions")} ({t("common.premium")}
                            )
                          </option>
                          <option value={20}>
                            20 {t("interview.questions")} ({t("common.premium")}
                            )
                          </option>
                          <option value={30}>
                            30 {t("interview.questions")} ({t("common.premium")}
                            )
                          </option>
                          <option value={50}>
                            50 {t("interview.questions")} ({t("common.premium")}
                            )
                          </option>
                        </>
                      )}
                    </select>
                  </motion.div>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <motion.div
              className="form-actions"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1 }}
            >
              <button
                type="button"
                onClick={() => navigate("/dashboard")}
                className="btn-secondary"
                disabled={formLoading}
              >
                Cancelar
              </button>
              <motion.button
                type="submit"
                disabled={formLoading}
                className="btn-primary"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {formLoading ? (
                  <>
                    <div className="spinner-small"></div>
                    {t("interview.creating")}
                  </>
                ) : (
                  <>
                    <FiPlusCircle />
                    {t("interview.create")}
                  </>
                )}
              </motion.button>
            </motion.div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default CreateInterview;
